import * as React from "react";
import { Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfirm } from "@/components/ui/use-confirm";
import { cn } from "@/lib/utils";
import { useBillingContext } from "@/contexts/BillingContext";
import { toast } from "sonner";
import { changePlan } from "../../services/billingApi";
import { presentMutationError } from "../../utils/showApiErrorToast";
import { beginPlanCheckout } from "./BillingActions";
import {
  billingIntervalTabs,
  checkoutIntervalForPaywallTab,
  LAUNCH_OFFER,
  SUBSCRIPTION_PLANS,
  monthlyFromAnnual,
  monthlyFromThreeMonth,
  paywallShowsIntroMonthlyPricing,
  type BillingInterval,
  type SubscriptionPlanDefinition,
} from "../../constants/subscriptionPlans";
import { shouldShowPaywallLaunchPricing } from "../../types/billing";

const brandTextClassName = "text-[color:var(--brand-tropical-surf)]";

function motionEnabled(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface ParsedAmount {
  prefix: string;
  value: number;
  decimals: number;
  suffix: string;
}

function parseAmount(amount: string): ParsedAmount | null {
  const match = /^([^\d]*)(\d+(?:\.\d+)?)(.*)$/.exec(amount.trim());
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  return {
    prefix,
    value: Number.parseFloat(digits),
    decimals: digits.includes(".") ? digits.split(".")[1].length : 0,
    suffix,
  };
}

const PRICE_COUNT_DURATION_MS = 420;

function useCountingAmount(amount: string): string {
  const target = React.useMemo(() => parseAmount(amount), [amount]);
  const [value, setValue] = React.useState(target?.value ?? 0);
  const fromRef = React.useRef(target?.value ?? 0);

  React.useEffect(() => {
    if (!target) return;

    if (!motionEnabled() || fromRef.current === target.value) {
      fromRef.current = target.value;
      setValue(target.value);
      return;
    }

    const from = fromRef.current;
    const start = performance.now();
    let frame = requestAnimationFrame(function tick(now: number) {
      const progress = Math.min((now - start) / PRICE_COUNT_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (target.value - from) * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        fromRef.current = target.value;
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [target]);

  if (!target) return amount;
  return `${target.prefix}${value.toFixed(target.decimals)}${target.suffix}`;
}

function useHasRendered(): boolean {
  const [rendered, setRendered] = React.useState(false);
  React.useEffect(() => setRendered(true), []);
  return rendered;
}

function PlanCheckoutButton({
  plan,
  interval,
  useIntroOfferCheckout,
  onThreeMonthOfferEnded,
  onCheckoutClick,
}: {
  plan: SubscriptionPlanDefinition;
  interval: BillingInterval;
  useIntroOfferCheckout: boolean;
  onThreeMonthOfferEnded: () => void;
  onCheckoutClick?: (planId: string) => void;
}) {
  const [loading, setLoading] = React.useState(false);
  const { status, refresh } = useBillingContext();
  const { confirm, dialog } = useConfirm();
  // Active subscribers switch in place (POST /billing/change-plan); everyone else goes to Checkout.
  const subscribed =
    ["active", "trialing"].includes(String(status?.status ?? "").toLowerCase()) && Boolean(status?.plan);
  const cadence: "month" | "year" = interval === "yearly" ? "year" : "month";
  const isCurrent =
    subscribed && status?.plan === plan.checkoutPlan && (status?.billing_interval ?? "month") === cadence;

  const handleSwitch = async () => {
    const price = plan.prices[interval];
    const ok = await confirm({
      title: `Switch to ${plan.name}?`,
      description: `${price.amount} ${cadence === "year" ? "per year" : "per month"}. The difference is charged (or credited) now, prorated. Your add-ons stay.`,
      confirmLabel: `Switch to ${plan.name}`,
      tone: "default",
    });
    if (!ok) return;
    setLoading(true);
    try {
      await changePlan(plan.checkoutPlan, cadence);
      await refresh();
      toast.success(`You're on ${plan.name} now`);
    } catch (e) {
      presentMutationError(e, "Couldn't switch your plan");
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    onCheckoutClick?.(plan.id);
    if (subscribed) {
      await handleSwitch();
      return;
    }
    setLoading(true);
    const redirected = await beginPlanCheckout(
      plan.checkoutPlan,
      checkoutIntervalForPaywallTab(interval, useIntroOfferCheckout),
      { onThreeMonthOfferEnded }
    );
    if (!redirected) setLoading(false);
  };

  return (
    <>
      {dialog}
      <Button
        type="button"
        variant={plan.highlighted ? "gradient" : "outline-brand"}
        size="lg"
        data-font="primary"
        data-paywall-plan-cta=""
        className="mt-6 w-full"
        disabled={loading || isCurrent}
        onClick={() => void handleClick()}
      >
        {isCurrent
          ? "Current plan"
          : loading
            ? subscribed ? "Switching…" : "Opening checkout…"
            : subscribed ? `Switch to ${plan.name}` : plan.cta}
      </Button>
    </>
  );
}

function PlanCard({
  plan,
  interval,
  showLaunchOfferPricing,
  onThreeMonthOfferEnded,
  onCheckoutClick,
}: {
  plan: SubscriptionPlanDefinition;
  interval: BillingInterval;
  showLaunchOfferPricing: boolean;
  onThreeMonthOfferEnded: () => void;
  onCheckoutClick?: (planId: string) => void;
}) {
  const price = plan.prices[interval];
  const showIntroMonthly = paywallShowsIntroMonthlyPricing(interval, showLaunchOfferPricing);
  const launchPrice = plan.prices.launch;
  const showLaunchOffer = showIntroMonthly && Boolean(launchPrice.launchOffer);
  const periodLabel =
    interval === "yearly" || showIntroMonthly ? "per month" : (price.period ?? "per month");
  const countingAmount = useCountingAmount(
    interval === "yearly"
      ? monthlyFromAnnual(price.amount)
      : showIntroMonthly
        ? monthlyFromThreeMonth(launchPrice.amount)
        : price.amount
  );

  return (
    <article
      data-paywall-plan-card={plan.id}
      className={cn(
        "flex flex-col rounded-[6px] p-6 sm:p-8",
        plan.highlighted
          ? "bg-[color:var(--brand-tropical-surf)]/10"
          : "bg-content-canvas"
      )}
    >
      <div
        data-paywall-plan-header=""
        className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2"
      >
        <h3
          data-paywall-plan-name=""
          className={cn("shrink-0 font-primary text-xl font-semibold", brandTextClassName)}
        >
          {plan.name}
        </h3>
        <div className="flex shrink-0 flex-nowrap items-center gap-2">
          {showLaunchOffer ? (
            <Badge
              data-paywall-plan-launch-badge=""
              className="h-5 rounded-[6px] border-0 bg-transparent bg-[image:var(--gradient-primary)] px-2 py-0.5 font-primary text-xs font-normal normal-case text-white"
            >
              {launchPrice.launchOffer}
            </Badge>
          ) : null}
          <Badge
            variant="outline"
            data-paywall-plan-usage-badge=""
            className="h-5 rounded-[6px] border-gray-500 px-2 py-0.5 font-primary text-xs font-normal normal-case text-gray-500"
          >
            {plan.usageLabel}
          </Badge>
        </div>
      </div>

      <p
        data-paywall-plan-description=""
        className="mt-2 text-sm leading-relaxed text-muted-foreground"
      >
        {plan.description}
      </p>

      <div data-paywall-plan-price-block="" className="mt-6">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            data-paywall-plan-price=""
            className={cn("font-primary text-4xl font-bold", brandTextClassName)}
          >
            {countingAmount}
          </span>
          <span
            key={periodLabel}
            data-paywall-plan-period=""
            className="text-sm text-muted-foreground"
          >
            {periodLabel}
          </span>
        </div>
        {interval === "yearly" ? (
          <p
            data-paywall-plan-annual-note=""
            className="mt-1 text-sm text-muted-foreground"
          >
            Billed {price.amount} annually
          </p>
        ) : null}
        {showIntroMonthly ? (
          <p
            data-paywall-plan-annual-note=""
            className="mt-1 text-sm text-muted-foreground"
          >
            for the first 3 months, then {plan.prices.monthly.amount} per month
          </p>
        ) : null}
      </div>

      <PlanCheckoutButton
        plan={plan}
        interval={interval}
        useIntroOfferCheckout={showLaunchOfferPricing}
        onThreeMonthOfferEnded={onThreeMonthOfferEnded}
        onCheckoutClick={onCheckoutClick}
      />

      <div data-paywall-plan-features="" className="mt-6 flex flex-col">
        <p
          data-paywall-plan-features-heading=""
          className={cn("font-primary text-sm font-medium", brandTextClassName)}
        >
          {plan.featuresHeading}
        </p>
        <ul
          data-paywall-plan-feature-list=""
          className="mt-3 space-y-3"
        >
          {plan.features.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-2.5 text-sm text-muted-foreground"
            >
              <Check className="mt-0.5 size-4 shrink-0 text-gray-400" aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

export interface SubscriptionPlanPickerProps {
  className?: string;
  onPlanCheckoutClick?: (planId: string) => void;
  /**
   * Demo / marketing surfaces: always show website-aligned launch pricing
   * (banner + intro Monthly amounts), even when billing status would hide it.
   * Checkout still falls back if the server ends the offer.
   */
  forceMarketingLaunchOffer?: boolean;
}

/**
 * Shared plan picker — launch banner, Monthly/Yearly tabs, Standard / Plus cards.
 * Used on the paywall modal and embedded surfaces (e.g. demo results).
 */
export function SubscriptionPlanPicker({
  className,
  onPlanCheckoutClick,
  forceMarketingLaunchOffer = false,
}: SubscriptionPlanPickerProps): React.ReactElement {
  const { status } = useBillingContext();
  const [offerEndedLocally, setOfferEndedLocally] = React.useState(false);
  const showLaunchOfferPricing =
    !offerEndedLocally &&
    (forceMarketingLaunchOffer ||
      shouldShowPaywallLaunchPricing(status, offerEndedLocally));
  const intervalTabs = billingIntervalTabs();
  const [interval, setInterval] = React.useState<BillingInterval>("monthly");
  const hasRendered = useHasRendered();
  const resolvedInterval: BillingInterval =
    interval === "yearly" ? "yearly" : "monthly";
  const showLaunchBanner =
    showLaunchOfferPricing && resolvedInterval === "monthly";

  const handleThreeMonthOfferEnded = React.useCallback(() => {
    setOfferEndedLocally(true);
    setInterval("monthly");
  }, []);

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      {showLaunchBanner ? (
        <div
          data-paywall-launch-banner=""
          data-state="open"
          data-animate={hasRendered ? "" : undefined}
          className="w-full overflow-hidden"
        >
          <div className="rounded-[6px] bg-[image:var(--gradient-primary)] px-6 py-5 text-center text-white sm:px-8 sm:py-6">
            <p className="font-primary text-xs font-semibold tracking-[0.08em] uppercase text-white/90">
              {LAUNCH_OFFER.badge}
            </p>
            <p className="mt-2 font-primary text-3xl leading-tight font-normal sm:text-4xl">
              {LAUNCH_OFFER.titleLead} {LAUNCH_OFFER.titleAccent}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-white/90 sm:text-base">
              {LAUNCH_OFFER.description}
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex w-full justify-center">
        <Tabs
          data-trade-log-tabs=""
          data-paywall-billing-tabs=""
          value={resolvedInterval}
          onValueChange={(value) => {
            setInterval(value as BillingInterval);
          }}
          className="w-fit !mb-0"
        >
          <div className="max-w-full overflow-x-auto overflow-y-hidden">
            <TabsList className="min-w-max">
              {intervalTabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>
      </div>

      <div className="grid w-full gap-4 lg:grid-cols-3 lg:gap-5">
        {SUBSCRIPTION_PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            interval={resolvedInterval}
            showLaunchOfferPricing={showLaunchOfferPricing}
            onThreeMonthOfferEnded={handleThreeMonthOfferEnded}
            onCheckoutClick={onPlanCheckoutClick}
          />
        ))}
      </div>
    </div>
  );
}
